/**
 * Calendar Event Editor Component
 * Main editor component for creating/editing events
 */

import { useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  CircularProgress,
  Alert,
  Snackbar,
  Paper,
  Button,
  Stack,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { Save, Publish, Delete, Cancel as CancelIcon } from '@mui/icons-material'
import { useState } from 'react'
import {
  CalendarEventDto,
  CreateCalendarEventRequest,
  UpdateCalendarEventRequest,
} from '@api/types/calendarEvent'
import {
  useCalendarEventEditorStore,
  selectFormData,
  selectSelectedTags,
  selectIsDirty,
  selectValidation,
  selectCanSave,
  selectCanPublish,
  selectCanDelete,
  selectCanCancel,
  selectIsEventPublished,
} from '../../stores/calendarEventEditorStore'
import {
  useCreateCalendarEvent,
  useUpdateCalendarEvent,
  usePublishCalendarEvent,
  useCancelCalendarEvent,
  useDeleteCalendarEvent,
  useSaveEventTags,
} from '../../hooks/useCalendarEvents'
import CalendarEventUnifiedEditor from './CalendarEventUnifiedEditor'

interface CalendarEventEditorProps {
  event?: CalendarEventDto
  isLoading?: boolean
}

export function CalendarEventEditor({ event, isLoading }: CalendarEventEditorProps) {
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // Store state
  const formData = useCalendarEventEditorStore(selectFormData)
  const selectedTags = useCalendarEventEditorStore(selectSelectedTags)
  const isDirty = useCalendarEventEditorStore(selectIsDirty)
  const validation = useCalendarEventEditorStore(selectValidation)
  const canSave = useCalendarEventEditorStore(selectCanSave)
  const canPublish = useCalendarEventEditorStore(selectCanPublish)
  const canDelete = useCalendarEventEditorStore(selectCanDelete)
  const canCancel = useCalendarEventEditorStore(selectCanCancel)
  const isPublished = useCalendarEventEditorStore(selectIsEventPublished)
  const isSaving = useCalendarEventEditorStore((state) => state.isSaving)
  const isPublishing = useCalendarEventEditorStore((state) => state.isPublishing)
  const setIsSaving = useCalendarEventEditorStore((state) => state.setIsSaving)
  const setIsPublishing = useCalendarEventEditorStore((state) => state.setIsPublishing)
  const setIsDirty = useCalendarEventEditorStore((state) => state.setIsDirty)
  const loadEvent = useCalendarEventEditorStore((state) => state.loadEvent)
  const validate = useCalendarEventEditorStore((state) => state.validate)
  const reset = useCalendarEventEditorStore((state) => state.reset)

  // Mutations
  const createMutation = useCreateCalendarEvent()
  const updateMutation = useUpdateCalendarEvent()
  const publishMutation = usePublishCalendarEvent()
  const cancelMutation = useCancelCalendarEvent()
  const deleteMutation = useDeleteCalendarEvent()
  const saveTagsMutation = useSaveEventTags()

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error' | 'info'
  }>({ open: false, message: '', severity: 'info' })

  // Load event into store when provided
  useEffect(() => {
    if (event) {
      loadEvent(event)
    }
    return () => {
      reset()
    }
  }, [event, loadEvent, reset])

  // Build request from form data
  const buildRequest = useCallback((): CreateCalendarEventRequest | UpdateCalendarEventRequest => {
    return {
      title: formData.title,
      quickDescription: formData.quickDescription,
      fullDescription: formData.fullDescription || undefined,
      startDateTime: formData.startDateTime?.toISOString() || '',
      endDateTime: formData.endDateTime?.toISOString() || '',
      timezone: formData.timezone || undefined,
      isAllDay: formData.isAllDay,
      locationName: formData.locationName || undefined,
      latitude: formData.latitude ?? undefined,
      longitude: formData.longitude ?? undefined,
      isPublic: formData.isPublic,
      maxAttendees: formData.maxAttendees ?? undefined,
      linkedAdventureId: formData.linkedAdventureId ?? undefined,
      coverImageId: formData.coverImageId ?? undefined,
      tags: selectedTags,
    }
  }, [formData, selectedTags])

  // Handle save
  const handleSave = useCallback(async () => {
    // Validate first
    const validationResult = validate()
    if (!validationResult.isValid) {
      setSnackbar({
        open: true,
        message: 'Please fix validation errors before saving',
        severity: 'error',
      })
      return
    }

    setIsSaving(true)

    try {
      if (event?.id) {
        // Update existing event
        await updateMutation.mutateAsync({
          id: event.id,
          data: buildRequest() as UpdateCalendarEventRequest,
        })

        // Save tags separately if they changed
        const originalTags = event.tags || []
        const tagsChanged =
          selectedTags.length !== originalTags.length ||
          !selectedTags.every((t) => originalTags.includes(t))

        if (tagsChanged) {
          await saveTagsMutation.mutateAsync({
            eventId: event.id,
            tagIds: selectedTags,
          })
        }

        setSnackbar({
          open: true,
          message: 'Event saved successfully',
          severity: 'success',
        })
        setIsDirty(false)
      } else {
        // Create new event
        const result = await createMutation.mutateAsync(
          buildRequest() as CreateCalendarEventRequest
        )

        // Extract event ID from response
        const newEventId = (result as any)?.data?.id || (result as any)?.id

        if (newEventId) {
          // Save tags for new event
          if (selectedTags.length > 0) {
            await saveTagsMutation.mutateAsync({
              eventId: newEventId,
              tagIds: selectedTags,
            })
          }

          setSnackbar({
            open: true,
            message: 'Event created successfully',
            severity: 'success',
          })

          // Navigate to edit page
          navigate(`/events/${newEventId}/edit`, { replace: true })
        }
      }
    } catch (error) {
      console.error('Failed to save event:', error)
      setSnackbar({
        open: true,
        message: 'Failed to save event. Please try again.',
        severity: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }, [
    event,
    buildRequest,
    validate,
    selectedTags,
    createMutation,
    updateMutation,
    saveTagsMutation,
    setIsSaving,
    setIsDirty,
    navigate,
  ])

  // Handle publish
  const handlePublish = useCallback(async () => {
    if (!event?.id) return

    // Validate first
    const validationResult = validate()
    if (!validationResult.isValid) {
      setSnackbar({
        open: true,
        message: 'Please fix validation errors before publishing',
        severity: 'error',
      })
      return
    }

    // Save first if dirty
    if (isDirty) {
      await handleSave()
    }

    setIsPublishing(true)

    try {
      await publishMutation.mutateAsync(event.id)
      setSnackbar({
        open: true,
        message: 'Event published successfully',
        severity: 'success',
      })
    } catch (error) {
      console.error('Failed to publish event:', error)
      setSnackbar({
        open: true,
        message: 'Failed to publish event. Please try again.',
        severity: 'error',
      })
    } finally {
      setIsPublishing(false)
    }
  }, [event, validate, isDirty, handleSave, publishMutation, setIsPublishing])

  // Handle cancel event
  const handleCancel = useCallback(async () => {
    if (!event?.id) return

    if (!window.confirm('Are you sure you want to cancel this event? Attendees will be notified.')) {
      return
    }

    try {
      await cancelMutation.mutateAsync({ id: event.id })
      setSnackbar({
        open: true,
        message: 'Event cancelled successfully',
        severity: 'success',
      })
    } catch (error) {
      console.error('Failed to cancel event:', error)
      setSnackbar({
        open: true,
        message: 'Failed to cancel event. Please try again.',
        severity: 'error',
      })
    }
  }, [event, cancelMutation])

  // Handle delete
  const handleDelete = useCallback(async () => {
    if (!event?.id) return

    if (!window.confirm('Are you sure you want to delete this event? This cannot be undone.')) {
      return
    }

    try {
      await deleteMutation.mutateAsync(event.id)
      setSnackbar({
        open: true,
        message: 'Event deleted successfully',
        severity: 'success',
      })
      navigate('/events/my-events')
    } catch (error) {
      console.error('Failed to delete event:', error)
      setSnackbar({
        open: true,
        message: 'Failed to delete event. Please try again.',
        severity: 'error',
      })
    }
  }, [event, deleteMutation, navigate])

  // Loading state
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      {/* Action Bar */}
      <Paper
        elevation={1}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          p: isMobile ? 1 : 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#fff',
          borderRadius: 0,
          mb: isMobile ? 1 : 2,
        }}
      >
        <Stack direction="row" spacing={isMobile ? 0.5 : 1}>
          {/* Save Button */}
          {canSave && (
            <Button
              variant="contained"
              size={isMobile ? 'small' : 'medium'}
              startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : <Save fontSize="small" />}
              onClick={handleSave}
              disabled={isSaving || isPublishing}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          )}

          {/* Publish Button */}
          {canPublish && !isPublished && (
            <Button
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
              startIcon={isPublishing ? <CircularProgress size={14} /> : <Publish fontSize="small" />}
              onClick={handlePublish}
              disabled={isSaving || isPublishing}
            >
              {isPublishing ? '...' : 'Publish'}
            </Button>
          )}
        </Stack>

        <Stack direction="row" spacing={isMobile ? 0.5 : 1}>
          {/* Cancel Event Button */}
          {canCancel && (
            <Button
              variant="outlined"
              color="warning"
              size={isMobile ? 'small' : 'medium'}
              startIcon={!isMobile && <CancelIcon fontSize="small" />}
              onClick={handleCancel}
            >
              {isMobile ? 'Cancel' : 'Cancel Event'}
            </Button>
          )}

          {/* Delete Button */}
          {canDelete && (
            <Button
              variant="outlined"
              color="error"
              size={isMobile ? 'small' : 'medium'}
              startIcon={!isMobile && <Delete fontSize="small" />}
              onClick={handleDelete}
            >
              Delete
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Content */}
      <Container maxWidth="md" sx={{ px: isMobile ? 1 : undefined }}>
        {/* Validation errors */}
        {!validation.isValid && (
          <Alert severity="error" sx={{ mb: isMobile ? 1 : 2 }}>
            Please fix the following errors:
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              {Object.entries(validation.errors).map(([field, error]) => (
                <li key={field}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Unified Editor */}
        <CalendarEventUnifiedEditor eventId={event?.id} />
      </Container>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default CalendarEventEditor
